import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isDoaga } from "../lib/chf";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Wallet, Plus, Trash2, Check, X, FileText, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { AuthImg } from "../components/AuthImg";
import { exportBrandedPDF, exportPayslipsPDF } from "../lib/pdfExport";
import { exportCSV } from "../lib/csvExport";
import { useConfirm } from "../components/ConfirmProvider";
import { fmtRoDate } from "../lib/dateFmt";

const STATUS_LABELS = { pending: "În așteptare", approved: "Aprobat", rejected: "Respins" };
const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-[var(--green-bg)] text-[var(--green-valid)]",
  rejected: "bg-zinc-100 text-zinc-500",
};

const monthOptions = (() => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("ro-RO", { year: "numeric", month: "long" }) });
  }
  return out;
})();

export default function Avansuri() {
  const { user, isAdmin } = useAuth();
  const [subs, setSubs] = useState([]);
  const [persons, setPersons] = useState([]);
  const [entries, setEntries] = useState([]);
  const [activeSub, setActiveSub] = useState("AVANSURI");
  const [month, setMonth] = useState(monthOptions[0].value);
  const [listMonth, setListMonth] = useState(monthOptions[0].value); // filtru lună pentru taburile de subcategorii
  const [openSubsDialog, setOpenSubsDialog] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [s, p, e] = await Promise.all([
        api.get("/salary-subcategories"),
        api.get("/persons").catch(() => ({ data: [] })),
        api.get("/salary-entries"),
      ]);
      setSubs(s.data || []);
      setPersons((p.data || []).filter((x) => x.active !== false));
      setEntries(e.data || []);
      if (!s.data?.find((x) => x.code === activeSub)) setActiveSub(s.data?.[0]?.code || "AVANSURI");
    } catch { toast.error("Eroare la încărcare"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!isAdmin && !user?.person_id) {
    return <div className="p-6 text-zinc-500 text-sm italic">Acces restricționat. Contul tău nu este legat de un angajat.</div>;
  }
  if (!isAdmin && (subs || []).length === 0) {
    return <div className="p-6 text-zinc-500 text-sm italic">Nu ai acces la nicio subcategorie din acest modul. Contactează administratorul.</div>;
  }

  if (isDoaga(user)) return <div className="text-sm text-zinc-500 p-6" data-testid="restricted-msg">Nu ai acces la această secțiune.</div>;
  return (
    <div className="space-y-4" data-testid="avansuri-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--primary-subtle)] flex items-center justify-center text-[var(--primary)]"><Wallet/></div>
          <div>
            <h1 className="font-display text-2xl font-bold">Avansuri / Cheltuieli / Amenzi</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Gestionare deduceri salariale și cheltuieli aprobate.</p>
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setOpenSubsDialog(true)} data-testid="manage-subs-btn"><Plus size={14} className="mr-1"/>Gestionează subcategorii</Button>
          )}
        </div>
      </div>

      <Tabs value={activeSub} onValueChange={setActiveSub}>
        <TabsList className="flex-wrap h-auto">
          {subs.map((s) => (
            <TabsTrigger key={s.code} value={s.code} data-testid={`tab-sub-${s.code}`}>
              <span className="w-2 h-2 rounded-full mr-1.5" style={{ background: s.color }}/>
              {s.label}
            </TabsTrigger>
          ))}
          {isAdmin && <TabsTrigger value="__summary__" data-testid="tab-summary">Sumar lunar</TabsTrigger>}
        </TabsList>

        {subs.map((s) => (
          <TabsContent key={s.code} value={s.code}>
            <SubcategoryView
              sub={s}
              persons={persons}
              entries={entries.filter((e) => e.subcategory_code === s.code)}
              listMonth={listMonth}
              onListMonthChange={setListMonth}
              onCreate={() => setCreating(true)}
              onEdit={(e) => setEditEntry(e)}
              onChanged={load}
            />
          </TabsContent>
        ))}

        {isAdmin && (
          <TabsContent value="__summary__">
            <SummaryView month={month} onMonthChange={setMonth} subs={subs} persons={persons} entries={entries} />
          </TabsContent>
        )}
      </Tabs>

      {creating && (
        <EntryDialog
          sub={subs.find((s) => s.code === activeSub)}
          persons={persons}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editEntry && (
        <EntryDialog
          entry={editEntry}
          sub={subs.find((s) => s.code === editEntry.subcategory_code)}
          persons={persons}
          onClose={() => setEditEntry(null)}
          onSaved={() => { setEditEntry(null); load(); }}
        />
      )}
      {openSubsDialog && (
        <SubsDialog subs={subs} onClose={() => setOpenSubsDialog(false)} onChanged={load} />
      )}
    </div>
  );
}

function SubcategoryView({ sub, persons, entries, listMonth, onListMonthChange, onCreate, onEdit, onChanged }) {
  const confirm = useConfirm();
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name"); // name | date | amount
  const filtered = entries
    .filter((e) => listMonth === "all" || (e.date || "").startsWith(listMonth))
    .filter((e) => statusFilter === "all" || e.status === statusFilter)
    .sort((a, b) => {
      if (sortBy === "name") {
        const n = (a.person_name || "").localeCompare(b.person_name || "", "ro");
        if (n !== 0) return n;
        return (b.date || "").localeCompare(a.date || "");
      }
      if (sortBy === "amount") return (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0);
      return (b.date || "").localeCompare(a.date || "");
    });
  const total = filtered.filter((e) => e.status === "approved").reduce((acc, e) => acc + (parseFloat(e.amount) || 0), 0);
  const pendingCount = entries.filter((e) => (listMonth === "all" || (e.date || "").startsWith(listMonth)) && e.status === "pending").length;

  const approve = async (id) => {
    try { await api.patch(`/salary-entries/${id}/approve`); toast.success("Aprobat"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Motiv respingere (opțional):") || null;
    try { await api.patch(`/salary-entries/${id}/reject`, { reject_reason: reason }); toast.success("Respins"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  const del = async (id) => {
    if (!(await confirm({ body: "Ștergi această înregistrare?", danger: true }))) return;
    try { await api.delete(`/salary-entries/${id}`); toast.success("Șters"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  return (
    <div className="space-y-3" data-testid={`sub-view-${sub.code}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 text-sm">
          <span className="text-zinc-500">Total aprobat:</span>
          <strong className="text-lg" style={{ color: sub.color }}>{total.toFixed(2)} CHF</strong>
          {pendingCount > 0 && sub.approval_required && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">{pendingCount} în așteptare</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={listMonth} onValueChange={onListMonthChange}>
            <SelectTrigger className="w-44 h-9" data-testid={`month-select-${sub.code}`}><SelectValue/></SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              <SelectItem value="all">Toate lunile</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 h-9" data-testid={`sort-select-${sub.code}`}><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sortare: Nume (A–Z)</SelectItem>
              <SelectItem value="date">Sortare: Dată (recent)</SelectItem>
              <SelectItem value="amount">Sortare: Sumă (mare→mică)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate</SelectItem>
              <SelectItem value="pending">În așteptare</SelectItem>
              <SelectItem value="approved">Aprobate</SelectItem>
              <SelectItem value="rejected">Respinse</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={onCreate} className="bg-[var(--primary)]" data-testid={`add-entry-${sub.code}`}><Plus size={14} className="mr-1"/>Adaugă</Button>
        </div>
      </div>

      {/* Mobile card view (<sm) */}
      <div className="sm:hidden space-y-2">
        {filtered.length === 0 && <div className="card-flat p-4 text-center text-sm text-zinc-400 italic">Nicio înregistrare.</div>}
        {filtered.map((e) => (
          <div key={e.id} className="card-flat p-3 text-sm" data-testid={`entry-card-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{e.person_name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{fmtRoDate(e.date)}{e.file_ids?.length > 0 ? ` · ${e.file_ids.length} 📎` : ""}</div>
              </div>
              <div className="text-right">
                <div className="font-bold tabular-nums">{(parseFloat(e.amount) || 0).toFixed(2)} CHF</div>
                <span className={`inline-block mt-0.5 text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[e.status]}`}>{STATUS_LABELS[e.status]}</span>
              </div>
            </div>
            {e.reason && <div className="text-xs text-zinc-600 mt-2 line-clamp-2">{e.from_acont && <span className="inline-block mr-1 text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-bold uppercase">Calendar Ore</span>}{e.reason}</div>}
            <div className="mt-2 pt-2 border-t border-[var(--border)] flex justify-end gap-1">
              {e.from_acont ? (
                <span className="text-[10px] text-zinc-400 italic">din Calendar Ore</span>
              ) : (
                <>
                  {e.status === "pending" && (
                    <>
                      <button onClick={() => approve(e.id)} className="text-xs px-2 py-1 rounded bg-[var(--green-bg)] text-[var(--green-valid)]" data-testid={`approve-mobile-${e.id}`}><Check size={12} className="inline"/> Aprobă</button>
                      <button onClick={() => reject(e.id)} className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-600"><X size={12} className="inline"/> Respinge</button>
                    </>
                  )}
                  <button onClick={() => onEdit(e)} className="text-xs px-2 py-1 rounded text-[var(--primary)]"><FileText size={12} className="inline"/> Edit</button>
                  <button onClick={() => del(e.id)} className="text-xs px-2 py-1 rounded text-red-600"><Trash2 size={12} className="inline"/></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table view (≥sm) */}
      <div className="hidden sm:block card-flat p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-[var(--border)]">
            <tr>
              <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Persoană</th>
              <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Data</th>
              <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Sumă</th>
              <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Motiv</th>
              <th className="p-2 text-center text-xs font-semibold text-zinc-500 uppercase">Status</th>
              <th className="p-2 text-center text-xs font-semibold text-zinc-500 uppercase">Atașamente</th>
              <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-center text-sm text-zinc-400 italic">Nicio înregistrare.</td></tr>
            )}
            {filtered.map((e) => (
              <tr key={e.id} className="border-b border-[var(--border)] last:border-b-0" data-testid={`entry-${e.id}`}>
                <td className="p-2 font-medium">{e.person_name}</td>
                <td className="p-2 text-zinc-600 tabular-nums">{fmtRoDate(e.date)}</td>
                <td className="p-2 text-right tabular-nums font-bold">{(parseFloat(e.amount) || 0).toFixed(2)} CHF</td>
                <td className="p-2 text-zinc-600 max-w-[260px] truncate" title={e.reason || ""}>
                  {e.from_acont && <span className="inline-block mr-1.5 text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-bold uppercase">Calendar Ore</span>}
                  {e.reason || "—"}
                </td>
                <td className="p-2 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[e.status]}`}>{STATUS_LABELS[e.status]}</span>
                </td>
                <td className="p-2 text-center">
                  {e.file_ids?.length > 0 ? <span className="text-xs text-zinc-500">{e.file_ids.length} 📎</span> : <span className="text-zinc-300">—</span>}
                </td>
                <td className="p-2 text-right whitespace-nowrap">
                  {e.from_acont ? (
                    <span className="text-[10px] text-zinc-400 italic" title="Se editează din pagina angajatului → Calendar Ore → ACONT">din Calendar Ore</span>
                  ) : (
                    <>
                      {e.status === "pending" && (
                        <>
                          <button onClick={() => approve(e.id)} className="text-[var(--green-valid)] p-1 hover:bg-[var(--green-bg)] rounded" title="Aprobă" data-testid={`approve-${e.id}`}><Check size={14}/></button>
                          <button onClick={() => reject(e.id)} className="text-amber-600 p-1 hover:bg-amber-50 rounded" title="Respinge" data-testid={`reject-${e.id}`}><X size={14}/></button>
                        </>
                      )}
                      <button onClick={() => onEdit(e)} className="text-[var(--primary)] p-1 hover:bg-[var(--primary-subtle)] rounded" title="Editează"><FileText size={14}/></button>
                      <button onClick={() => del(e.id)} className="text-red-600 p-1 hover:bg-red-50 rounded" title="Șterge"><Trash2 size={14}/></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.some((e) => e.file_ids?.length > 0) && (
        <div className="card-flat p-4">
          <h4 className="font-display text-sm font-bold mb-3">Atașamente (preview)</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {filtered.flatMap((e) => (e.file_ids || []).map((fid) => ({ fid, e }))).map(({ fid, e }, idx) => (
              <a key={`${e.id}-${fid}-${idx}`} href={`/api/files/${fid}`} target="_blank" rel="noreferrer" className="block aspect-square rounded border border-[var(--border)] overflow-hidden hover:border-[var(--primary)]">
                <AuthImg fileId={fid} alt={e.reason || ""} className="w-full h-full object-cover"/>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EntryDialog({ entry, sub, persons, onClose, onSaved }) {
  const editing = !!entry;
  const [form, setForm] = useState(() => ({
    person_id: entry?.person_id || "",
    subcategory_code: entry?.subcategory_code || sub?.code || "",
    amount: entry?.amount || "",
    date: entry?.date || new Date().toISOString().slice(0, 10),
    reason: entry?.reason || "",
    file_ids: entry?.file_ids || [],
  }));
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: f } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, file_ids: [...prev.file_ids, f.id] }));
      toast.success("Fișier încărcat");
    } catch { toast.error("Eroare la upload"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.person_id) { toast.error("Selectează persoana"); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Introdu o sumă pozitivă"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/salary-entries/${entry.id}`, { ...form, amount: parseFloat(form.amount) });
      else await api.post("/salary-entries", { ...form, amount: parseFloat(form.amount) });
      toast.success(editing ? "Actualizat" : "Adăugat");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="entry-dialog">
        <DialogHeader><DialogTitle>{editing ? "Editare" : "Adaugă"} · {sub?.label || form.subcategory_code}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Persoană</Label>
            <Select value={form.person_id} onValueChange={(v) => setForm({ ...form, person_id: v })}>
              <SelectTrigger className="mt-1" data-testid="entry-person"><SelectValue placeholder="Selectează angajatul"/></SelectTrigger>
              <SelectContent>
                {persons.map((p) => <SelectItem key={p.id} value={p.id}>{p.nume} {p.prenume}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Suma (CHF)</Label><Input type="number" min={0.01} step={0.01} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="entry-amount"/></div>
            <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="entry-date"/></div>
          </div>
          <div>
            <Label>Motiv / observații</Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="ex: avans pe luna aprilie" data-testid="entry-reason"/>
          </div>
          <div>
            <Label>Atașamente</Label>
            <label className="mt-1 flex items-center gap-2 px-3 py-2 border border-dashed border-[var(--border)] rounded cursor-pointer hover:border-[var(--primary)] text-sm text-zinc-600">
              <Upload size={14}/>
              {uploading ? "Se încarcă..." : "Încarcă imagine / dovadă"}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} data-testid="entry-file-input"/>
            </label>
            {form.file_ids.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.file_ids.map((fid) => (
                  <div key={fid} className="text-xs px-2 py-1 rounded bg-zinc-100 flex items-center gap-1">
                    📎 {fid.slice(0, 8)}…
                    <button onClick={() => setForm({ ...form, file_ids: form.file_ids.filter((x) => x !== fid) })} className="text-red-600 ml-1"><X size={10}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={busy} className="bg-[var(--primary)]" data-testid="entry-submit">{busy ? "Se salvează..." : "Salvează"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubsDialog({ subs, onClose, onChanged }) {
  const confirm = useConfirm();
  const [form, setForm] = useState({ code: "", label: "", color: "#3B82F6", approval_required: false });

  const submit = async () => {
    try { await api.post("/salary-subcategories", form); toast.success("Subcategorie adăugată"); setForm({ code: "", label: "", color: "#3B82F6", approval_required: false }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  const del = async (code) => {
    if (!(await confirm({ body: `Ștergi subcategoria ${code}?`, danger: true }))) return;
    try { await api.delete(`/salary-subcategories/${code}`); toast.success("Ștearsă"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="subs-dialog">
        <DialogHeader><DialogTitle>Subcategorii</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="card-flat p-3 bg-zinc-50">
            <div className="text-xs font-semibold text-zinc-500 uppercase mb-2">Existente</div>
            <ul className="space-y-1.5">
              {subs.map((s) => (
                <li key={s.code} className="flex items-center gap-2 text-sm">
                  <span className="w-3 h-3 rounded-full" style={{ background: s.color }}/>
                  <span className="font-mono text-xs">{s.code}</span>
                  <span className="flex-1">{s.label}</span>
                  {s.approval_required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Aprobare</span>}
                  {s.system ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600">Sistem</span> : (
                    <button onClick={() => del(s.code)} className="text-red-600 p-1"><Trash2 size={12}/></button>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t border-[var(--border)] pt-3">
            <div className="text-xs font-semibold text-zinc-500 uppercase mb-2">Adaugă subcategorie nouă</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cod</Label><Input className="font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="BONUSURI"/></div>
              <div><Label>Etichetă</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Bonusuri"/></div>
              <div><Label>Culoare</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}/></div>
              <div className="flex items-center gap-2 mt-6">
                <input type="checkbox" id="ar" checked={form.approval_required} onChange={(e) => setForm({ ...form, approval_required: e.target.checked })}/>
                <Label htmlFor="ar" className="text-xs">Necesită aprobare</Label>
              </div>
            </div>
            <Button onClick={submit} className="mt-3 bg-[var(--primary)]" data-testid="add-sub-btn">Adaugă</Button>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Închide</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryView({ month, onMonthChange, subs, persons = [], entries = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/salary-summary?month=${month}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Eroare la încărcare"))
      .finally(() => setLoading(false));
  }, [month]);

  const subCodes = subs.map((s) => s.code);
  const totals = useMemo(() => {
    if (!data) return null;
    return data.rows.reduce((acc, r) => {
      acc.salary_brut += r.salary_brut;
      acc.deductions += r.total_deductions;
      acc.salary_net += r.salary_net;
      acc.hours += r.hours;
      return acc;
    }, { salary_brut: 0, deductions: 0, salary_net: 0, hours: 0 });
  }, [data]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="small-label">Luna</Label>
        <Select value={month} onValueChange={onMonthChange}>
          <SelectTrigger className="w-56 h-9" data-testid="summary-month"><SelectValue/></SelectTrigger>
          <SelectContent>
            {monthOptions.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {totals && (
          <div className="ml-auto flex gap-4 text-sm flex-wrap items-center">
            <div><span className="text-zinc-500">Brut total:</span> <strong>{totals.salary_brut.toFixed(2)} CHF</strong></div>
            <div><span className="text-zinc-500">Deduceri:</span> <strong className="text-red-600">−{totals.deductions.toFixed(2)} CHF</strong></div>
            <div><span className="text-zinc-500">Net:</span> <strong className="text-[var(--green-valid)]">{totals.salary_net.toFixed(2)} CHF</strong></div>
            <Button
              variant="outline"
              size="sm"
              data-testid="export-salary-pdf"
              onClick={async () => {
                try {
                  const monthLabel = monthOptions.find((m) => m.value === month)?.label || month;
                  await exportBrandedPDF({
                    title: `Sumar salarii — ${monthLabel}`,
                    subtitle: `${data.rows.length} persoane active · Brut ${totals.salary_brut.toFixed(2)} CHF · Deduceri −${totals.deductions.toFixed(2)} CHF · Net ${totals.salary_net.toFixed(2)} CHF`,
                    sections: [
                      {
                        heading: "Sumar lună",
                        summary: [
                          ["Persoane active", data.rows.length],
                          ["Total om-ore", data.rows.reduce((a, r) => a + r.hours, 0).toFixed(1)],
                          ["Salariu brut total", `${totals.salary_brut.toFixed(2)} CHF`],
                          ["Total deduceri", `−${totals.deductions.toFixed(2)} CHF`],
                          ["Salariu net total", `${totals.salary_net.toFixed(2)} CHF`],
                        ],
                      },
                      {
                        heading: "Detaliu per persoană",
                        table: {
                          columns: ["Persoană", "Ore", "Condus", "Salar ore", "Diurnă", "Brut", ...subCodes, "Net"],
                          rows: data.rows.map((r) => [
                            r.person_name,
                            r.hours.toFixed(1),
                            (r.hours_driven || 0).toFixed(1),
                            r.salary_hours.toFixed(2),
                            r.diurna.toFixed(2),
                            r.salary_brut.toFixed(2),
                            ...subCodes.map((c) => r.deductions[c] ? `−${r.deductions[c].toFixed(2)}` : "—"),
                            r.salary_net.toFixed(2),
                          ]),
                        },
                      },
                    ],
                    filename: `sumar-salarii-${month}.pdf`,
                  });
                  toast.success("PDF generat");
                } catch { toast.error("Eroare la generare PDF"); }
              }}
            >
              <FileText size={14} className="mr-1.5"/> Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="export-payslips-pdf"
              onClick={async () => {
                try {
                  const monthLabel = monthOptions.find((m) => m.value === month)?.label || month;
                  const subsByCode = Object.fromEntries(subs.map((s) => [s.code, s.label]));
                  const personsById = Object.fromEntries(persons.map((p) => [p.id, p]));
                  const firmas = (await api.get("/employer-companies").catch(() => ({ data: [] }))).data || [];
                  const firmaById = Object.fromEntries(firmas.map((f) => [f.id, f.name]));
                  // Approved entries only — filter to selected month — group by person_id
                  const monthEntries = (entries || []).filter(
                    (e) => e.status === "approved" && (e.date || "").startsWith(month)
                  );
                  const entriesByPerson = monthEntries.reduce((acc, e) => {
                    (acc[e.person_id] = acc[e.person_id] || []).push(e);
                    return acc;
                  }, {});
                  const payslips = data.rows
                    .filter((r) => r.hours > 0 || r.total_deductions > 0)
                    .map((r) => {
                      const p = personsById[r.person_id] || {};
                      const personEntries = (entriesByPerson[r.person_id] || []).map((e) => ({
                        date: e.date,
                        code: e.subcategory_code,
                        label: subsByCode[e.subcategory_code] || e.subcategory_code,
                        reason: e.reason,
                        amount: Number(e.amount) || 0,
                      }));
                      personEntries.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
                      return {
                        person_name: r.person_name,
                        person_firma: p.firma_id ? firmaById[p.firma_id] : null,
                        hours: r.hours,
                        hours_driven: r.hours_driven || 0,
                        days_worked: r.days_worked,
                        hourly_rate: p.hourly_rate || null,
                        salary_hours: r.salary_hours,
                        diurna: r.diurna,
                        salary_brut: r.salary_brut,
                        deductions: personEntries,
                        total_deductions: r.total_deductions,
                        salary_net: r.salary_net,
                      };
                    });
                  if (payslips.length === 0) {
                    toast.info("Nicio activitate sau deducere în luna selectată.");
                    return;
                  }
                  await exportPayslipsPDF({
                    month, monthLabel, payslips,
                    filename: `fluturasi-${month}.pdf`,
                  });
                  toast.success(`${payslips.length} fluturași generați`);
                } catch (e) { console.error(e); toast.error("Eroare la generare fluturași"); }
              }}
            >
              <FileText size={14} className="mr-1.5"/> Export fluturași
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="export-salary-csv"
              onClick={() => {
                try {
                  const cols = [
                    { key: "person_name", label: "Persoană" },
                    { key: (r) => r.hours.toFixed(1), label: "Ore" },
                    { key: (r) => (r.hours_driven || 0).toFixed(1), label: "Ore conduse" },
                    { key: (r) => r.days_worked, label: "Zile lucrate" },
                    { key: (r) => r.salary_hours.toFixed(2), label: "Salar ore (CHF)" },
                    { key: (r) => r.diurna.toFixed(2), label: "Diurnă (CHF)" },
                    { key: (r) => r.salary_brut.toFixed(2), label: "Brut (CHF)" },
                    ...subCodes.map((c) => ({ key: (r) => (r.deductions[c] || 0).toFixed(2), label: `${c} (CHF)` })),
                    { key: (r) => r.total_deductions.toFixed(2), label: "Total deduceri (CHF)" },
                    { key: (r) => r.salary_net.toFixed(2), label: "Net (CHF)" },
                  ];
                  exportCSV(data.rows, cols, `sumar-salarii-${month}.csv`);
                  toast.success("CSV generat");
                } catch { toast.error("Eroare la generare CSV"); }
              }}
            >
              <Download size={14} className="mr-1.5"/> Export CSV
            </Button>
          </div>
        )}
      </div>

      {loading ? <div className="text-sm text-zinc-400 italic py-8 text-center">Se încarcă…</div> : (
        <>
          {/* Mobile card view (<sm) */}
          <div className="sm:hidden space-y-2">
            {(data?.rows || []).map((r) => (
              <div key={r.person_id} className="card-flat p-3 text-sm" data-testid={`summary-card-${r.person_id}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <strong className="truncate">{r.person_name}</strong>
                  <span className="text-xs text-zinc-500 tabular-nums">{r.hours.toFixed(1)} h{(r.hours_driven || 0) > 0 ? ` + 🚗${(r.hours_driven || 0).toFixed(1)}h` : ""} · {r.days_worked}z</span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div className="text-zinc-500">Salar ore</div><div className="text-right tabular-nums">{r.salary_hours.toFixed(2)}</div>
                  <div className="text-zinc-500">Diurnă</div><div className="text-right tabular-nums text-zinc-500">{r.diurna.toFixed(2)}</div>
                  <div className="font-medium pt-1 border-t border-[var(--border)] mt-1">Brut</div>
                  <div className="text-right tabular-nums font-bold pt-1 border-t border-[var(--border)] mt-1">{r.salary_brut.toFixed(2)}</div>
                  {subCodes.map((c) => r.deductions[c] ? (
                    <React.Fragment key={c}>
                      <div className="text-zinc-500">{c}</div>
                      <div className="text-right tabular-nums text-red-600">−{r.deductions[c].toFixed(2)}</div>
                    </React.Fragment>
                  ) : null)}
                  <div className="font-bold pt-1 border-t border-[var(--border)] mt-1">Net</div>
                  <div className="text-right tabular-nums font-bold text-[var(--green-valid)] pt-1 border-t border-[var(--border)] mt-1">{r.salary_net.toFixed(2)} CHF</div>
                </div>
              </div>
            ))}
            {data?.rows?.length === 0 && <div className="card-flat p-4 text-center text-zinc-400 italic">Niciun angajat activ.</div>}
          </div>
          {/* Desktop table view (≥sm) */}
          <div className="hidden sm:block card-flat p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-[var(--border)]">
                <tr>
                  <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Persoană</th>
                  <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Ore</th>
                  <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">🚗 Condus</th>
                  <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Salar ore</th>
                  <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Diurnă</th>
                  <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase border-l border-[var(--border)]">Brut</th>
                  {subCodes.map((c) => (
                    <th key={c} className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">{c}</th>
                  ))}
                  <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase border-l border-[var(--border)]">Net</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((r) => (
                  <tr key={r.person_id} className="border-b border-[var(--border)] last:border-b-0">
                    <td className="p-2 font-medium">{r.person_name}</td>
                    <td className="p-2 text-right tabular-nums">{r.hours.toFixed(1)}</td>
                    <td className="p-2 text-right tabular-nums text-amber-600">{(r.hours_driven || 0).toFixed(1)}</td>
                    <td className="p-2 text-right tabular-nums">{r.salary_hours.toFixed(2)}</td>
                    <td className="p-2 text-right tabular-nums text-zinc-500">{r.diurna.toFixed(2)}</td>
                    <td className="p-2 text-right tabular-nums font-bold border-l border-[var(--border)]">{r.salary_brut.toFixed(2)}</td>
                    {subCodes.map((c) => (
                      <td key={c} className="p-2 text-right tabular-nums text-red-600">
                        {r.deductions[c] ? `−${r.deductions[c].toFixed(2)}` : "—"}
                      </td>
                    ))}
                    <td className="p-2 text-right tabular-nums font-bold border-l border-[var(--border)] text-[var(--green-valid)]">{r.salary_net.toFixed(2)}</td>
                  </tr>
                ))}
                {data?.rows?.length === 0 && (
                  <tr><td colSpan={7 + subCodes.length} className="p-4 text-center text-zinc-400 italic">Niciun angajat activ.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p className="text-[11px] text-zinc-500 italic">Calcul: <strong>Salariu net = ((Ore + Ore conduse) × tarif orar) + Diurnă (16 CHF/zi doar dacă Ore + Ore conduse ≥ 5h) − Total deduceri aprobate</strong></p>
    </div>
  );
}
